import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {InfoLine} from '@dropbox/dig-icons/assets';
import {EditorDocStrategyType} from 'client/models/EditorDocStrategyType';
import {usePlanningId} from 'components/DSYS/CollabProvider';
import {markdownTable} from 'components/DSYS/editor/dynamicNode/markdownExport';
import {
  mergeEditorStateMapIntoObjectFieldWithSource,
  sourceTextKeyFor,
} from 'components/DSYS/editor/dynamicNode/overlayHelpers';
import {PlanningCommentField} from 'components/DSYS/editor/dynamicNode/PlanningCommentField';
import {planningMapKeys} from 'components/DSYS/editor/dynamicNode/planningCommentMapKeys';
import {getTableWidthPx} from 'components/DSYS/editor/dynamicNode/planningTable/getTableWidthPx';
import {
  PLANNING_TABLE_SHELL_STYLE,
  PlanningTableWideShell,
} from 'components/DSYS/editor/dynamicNode/planningTable/WideTableShell';
import type {
  DynamicNodeDefinition,
  DynamicNodeRenderProps,
} from 'components/DSYS/editor/dynamicNode/types';
import {
  type MergePersistedMapKeyArgs,
  useDynamicNodeEditorStateMap,
} from 'components/DSYS/editor/dynamicNode/useDynamicNodeEditorStateMap';
import {$isDynamicNode} from 'components/DSYS/editor/nodes/DynamicNode';
import {
  getStrategyEntryPlainText,
  normalizeStrategyEntries,
  normalizeStrategySummary,
  type StrategyEntry,
} from 'components/DSYS/editor/nodes/strategyEntries';
import {StrategyEntriesRichTextListEditor} from 'components/DSYS/editor/nodes/StrategyEntriesRichTextListEditor';
import {
  PrimaryWorkstreamMetricWrapper,
  StarredMilestonesWrapper,
  SubworkstreamSelectionWrapper,
  WorkstreamObjectiveEditableWrapper,
} from 'components/DSYS/editor/nodes/WorkstreamEditComponent';
import {parseAndExtractTextFromLexicalJson} from 'components/DSYS/editor/utils/lexical';
import type {Column} from 'components/DSYS/Table';
import {Table} from 'components/DSYS/Table';
import {t} from 'i18next';
import {$getNodeByKey} from 'lexical';
import {type ReactNode, useCallback, useEffect, useMemo} from 'react';
import {usePlanning, useStrategiesUpdate, useStrategyEntries} from 'views/planning/hooks';
import {useWorkstream} from 'views/workstreams/hooks';
import {
  isFullWorkstreamMetric,
  isPrimaryMetric,
  isWorkstreamStatusClosed,
} from 'views/workstreams/utils';

import styles from './StrategyKernel.module.css';
import {
  formatStrategyKernelSixMonthPlanExport,
  getStrategyKernelNorthStarMetric,
} from './strategyKernelPlan';

export const STRATEGY_KERNEL_KIND = 'strategy_kernel';

type StrategyKernelBinding = {
  kind: typeof STRATEGY_KERNEL_KIND;
};

type StrategyKernelValue = {
  /**
   * Cached for export only - DO NOT use for rendering (use backend data instead)
   */
  obstaclesAndOpportunities?: StrategyEntry[];
  guidingPrinciples?: StrategyEntry[];
  /**
   * JSON string: `Record<map key, serialized Lexical>` for planning collab comment highlights.
   * Map key examples:
   * - Column headers: `__columnHeader:*` keys
   * - Strategy entries: `{nodeType}:{index}:{contentHash}` keys (e.g., `obstacles-and-opportunities:0:a1b2c3d4`)
   */
  editorStateMap?: string;
  objectiveContentEditorState?: string;
  leadMetricEditorStateMap?: string;
  lagMetricEditorStateMap?: string;
  milestonesEditorStateMap?: string;
  subworkstreamsEditorStateMap?: string;
  objectiveFallbackText?: string;
  leadMetricItems?: StrategyKernelExportItem[];
  lagMetricItems?: StrategyKernelExportItem[];
  milestoneItems?: StrategyKernelExportItem[];
  subworkstreamItems?: StrategyKernelExportItem[];
};

type StrategyKernelEditorStateMapField =
  | 'leadMetricEditorStateMap'
  | 'lagMetricEditorStateMap'
  | 'milestonesEditorStateMap'
  | 'subworkstreamsEditorStateMap';

const STRATEGY_KERNEL_SCOPED_MAP_FIELDS: StrategyKernelEditorStateMapField[] = [
  'leadMetricEditorStateMap',
  'lagMetricEditorStateMap',
  'milestonesEditorStateMap',
  'subworkstreamsEditorStateMap',
];

/** Parses keys like `leadMetricEditorStateMap:123:owner` (inner key `123:owner`). */
function splitStrategyKernelScopedMapKey(
  mapKey: string
): {field: StrategyKernelEditorStateMapField; innerKey: string} | undefined {
  for (const field of STRATEGY_KERNEL_SCOPED_MAP_FIELDS) {
    const prefix = `${field}:`;
    if (mapKey.startsWith(prefix)) {
      return {field, innerKey: mapKey.slice(prefix.length)};
    }
  }
  return undefined;
}

const STRATEGY_KERNEL_PLAN_SECTION_PREFIX = 'strategy-kernel-plan-section:' as const;

type StrategyKernelRow = {
  kind: 'strategy_kernel';
};

type StrategyKernelExportItem = {
  id: number;
  text: string;
};

const STRATEGY_KERNEL_ROWS: StrategyKernelRow[] = [{kind: 'strategy_kernel'}];

const STRATEGY_KERNEL_COLUMNS: Column<StrategyKernelRow>[] = [
  {
    type: 'strategy_kernel_objective',
    minWidth: 220,
    cantDisable: true,
  },
  {
    type: 'strategy_kernel_obstacles_and_opportunities',
    minWidth: 240,
    cantDisable: true,
  },
  {
    type: 'strategy_kernel_guiding_policies',
    minWidth: 240,
    cantDisable: true,
  },
  {
    type: 'strategy_kernel_six_month_plan',
    minWidth: 320,
    cantDisable: true,
  },
];

const STRATEGY_KERNEL_COLUMN_LABEL_OVERRIDES = {
  strategy_kernel_objective: 'strategy_kernel_objective',
  strategy_kernel_obstacles_and_opportunities: 'strategy_kernel_obstacles_and_opportunities',
  strategy_kernel_guiding_policies: 'strategy_kernel_guiding_policies',
  strategy_kernel_six_month_plan: 'strategy_kernel_six_month_plan',
} as const;

const STRATEGY_KERNEL_WIDTH_PX = getTableWidthPx(STRATEGY_KERNEL_COLUMNS);

function parseStrategyKernelValue(raw: unknown): StrategyKernelValue {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  const value = raw as Record<string, unknown>;
  return {
    obstaclesAndOpportunities:
      'obstaclesAndOpportunities' in value
        ? normalizeStrategyEntries(value.obstaclesAndOpportunities)
        : undefined,
    guidingPrinciples:
      'guidingPrinciples' in value ? normalizeStrategyEntries(value.guidingPrinciples) : undefined,
    editorStateMap: typeof value.editorStateMap === 'string' ? value.editorStateMap : undefined,
    objectiveContentEditorState:
      typeof value.objectiveContentEditorState === 'string'
        ? value.objectiveContentEditorState
        : undefined,
    leadMetricEditorStateMap:
      typeof value.leadMetricEditorStateMap === 'string'
        ? value.leadMetricEditorStateMap
        : undefined,
    lagMetricEditorStateMap:
      typeof value.lagMetricEditorStateMap === 'string' ? value.lagMetricEditorStateMap : undefined,
    milestonesEditorStateMap:
      typeof value.milestonesEditorStateMap === 'string'
        ? value.milestonesEditorStateMap
        : undefined,
    subworkstreamsEditorStateMap:
      typeof value.subworkstreamsEditorStateMap === 'string'
        ? value.subworkstreamsEditorStateMap
        : undefined,
    objectiveFallbackText:
      typeof value.objectiveFallbackText === 'string' ? value.objectiveFallbackText : undefined,
    leadMetricItems: parseExportItems(value.leadMetricItems),
    lagMetricItems: parseExportItems(value.lagMetricItems),
    milestoneItems: parseExportItems(value.milestoneItems),
    subworkstreamItems: parseExportItems(value.subworkstreamItems),
  };
}

function parseExportItems(raw: unknown): StrategyKernelExportItem[] | undefined {
  if (!Array.isArray(raw)) {
    return undefined;
  }
  const items = raw.filter(
    (item): item is StrategyKernelExportItem =>
      !!item &&
      typeof item === 'object' &&
      typeof (item as StrategyKernelExportItem).id === 'number' &&
      Number.isFinite((item as StrategyKernelExportItem).id) &&
      typeof (item as StrategyKernelExportItem).text === 'string'
  );
  return items.length > 0 ? items : undefined;
}

function updateJsonMapField(
  base: StrategyKernelValue,
  field: StrategyKernelEditorStateMapField,
  mapKey: string,
  serializedState: string | undefined,
  sourceText: string | undefined
): StrategyKernelValue {
  const currentJson = base[field];
  const map: Record<string, string> = {};
  if (currentJson) {
    try {
      Object.assign(map, JSON.parse(currentJson) as Record<string, string>);
    } catch {
      /* ignore malformed persisted state */
    }
  }
  const srcKey = sourceTextKeyFor(mapKey);
  if (!serializedState) {
    delete map[mapKey];
    delete map[srcKey];
  } else {
    map[mapKey] = serializedState;
    if (sourceText !== undefined) {
      map[srcKey] = sourceText;
    } else {
      delete map[srcKey];
    }
  }
  const next = {...base};
  if (Object.keys(map).length === 0) {
    delete next[field];
  } else {
    next[field] = JSON.stringify(map);
  }
  return next;
}

function summarizeStrategyEntries(entries: StrategyEntry[] | undefined): string {
  const text = (entries ?? []).map(getStrategyEntryPlainText).filter(Boolean).join('; ');
  return text || '-';
}

function summarizeEditorState(raw: string | undefined): string {
  if (!raw) {
    return '';
  }
  try {
    const parsed = JSON.parse(raw) as
      | {root?: unknown}
      | Record<string, string | undefined>
      | Array<unknown>;

    if (parsed && typeof parsed === 'object' && 'root' in parsed) {
      return parseAndExtractTextFromLexicalJson(raw).trim();
    }

    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return Object.values(parsed)
        .filter((entry): entry is string => typeof entry === 'string')
        .map((entry) => {
          try {
            return parseAndExtractTextFromLexicalJson(entry).trim();
          } catch {
            return entry.trim();
          }
        })
        .filter(Boolean)
        .join('; ');
    }
  } catch {
    return raw.trim();
  }
  return '';
}

function summarizeSingleEditorState(
  raw: string | undefined,
  fallbackText: string | undefined
): string {
  return summarizeEditorState(raw) || fallbackText || '';
}

function parseEditorStateMap(raw: string | undefined): Record<string, string> {
  if (!raw) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw) as Record<string, string | undefined>;
    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, string] => typeof entry[1] === 'string'
      )
    );
  } catch {
    return {};
  }
}

function summarizeScopedEditorStateMap(
  raw: string | undefined,
  items: StrategyKernelExportItem[] | undefined
): string {
  if (!items || items.length === 0) {
    return '';
  }
  const byId = parseEditorStateMap(raw);
  return items
    .map((item) => summarizeSingleEditorState(byId[String(item.id)], item.text))
    .filter(Boolean)
    .join('; ');
}

function exportStrategyKernel(value: unknown): string {
  const parsed = parseStrategyKernelValue(value);
  const northStarSummary =
    summarizeScopedEditorStateMap(parsed.lagMetricEditorStateMap, parsed.lagMetricItems) || '-';
  const milestoneSummary = summarizeScopedEditorStateMap(
    parsed.milestonesEditorStateMap,
    parsed.milestoneItems
  );
  const subworkstreamSummary = summarizeScopedEditorStateMap(
    parsed.subworkstreamsEditorStateMap,
    parsed.subworkstreamItems
  );
  const sixMonthPlan = formatStrategyKernelSixMonthPlanExport({
    northStarSummary,
    milestoneSummary,
    subworkstreamSummary,
  });

  return markdownTable(
    [
      t('strategy_kernel_objective'),
      t('strategy_kernel_obstacles_and_opportunities'),
      t('strategy_kernel_guiding_policies'),
      t('strategy_kernel_six_month_plan'),
    ],
    [
      [
        summarizeSingleEditorState(
          parsed.objectiveContentEditorState,
          parsed.objectiveFallbackText
        ) || '-',
        summarizeStrategyEntries(parsed.obstaclesAndOpportunities),
        summarizeStrategyEntries(parsed.guidingPrinciples),
        sixMonthPlan || '-',
      ],
    ]
  );
}

const StrategyKernel = ({editor, nodeKey, value, setValue}: DynamicNodeRenderProps) => {
  const planningId = usePlanningId();
  const {planning} = usePlanning(planningId ?? '');
  const switchboardId = planning?.switchboard_id ?? undefined;
  const {workstream: switchboardWorkstream} = useWorkstream(switchboardId);
  const {data: strategyEntries} = useStrategyEntries(planningId ?? '');
  const {createStrategy, updateStrategy, deleteStrategy} = useStrategiesUpdate();
  const nodeValue = useMemo(() => parseStrategyKernelValue(value), [value]);
  const persistedStrategySummary = useMemo(
    () => normalizeStrategySummary(strategyEntries),
    [strategyEntries]
  );

  const patchNodeValue = useCallback(
    (patch: Partial<StrategyKernelValue>) => {
      let next: StrategyKernelValue | undefined;
      editor.getEditorState().read(() => {
        const node = $getNodeByKey(nodeKey);
        if (!$isDynamicNode(node)) {
          return;
        }
        next = {...parseStrategyKernelValue(node.getValue()), ...patch};
      });
      if (next !== undefined) {
        setValue(next);
      }
    },
    [editor, nodeKey, setValue]
  );

  const mergePersistedField = useCallback(
    ({rawNodeValue, mapKey, serializedState, sourceText}: MergePersistedMapKeyArgs) => {
      // Column headers and strategy entries both use editorStateMap
      const isStrategyEntry =
        (mapKey.startsWith('obstacles-and-opportunities:') ||
          mapKey.startsWith('guiding-principles:')) &&
        !mapKey.startsWith('__');
      const isColumnHeader = mapKey.startsWith('__columnHeader:');
      const isWorkstreamObjective = mapKey.endsWith(':objective');

      if (isStrategyEntry || isColumnHeader || isWorkstreamObjective) {
        return mergeEditorStateMapIntoObjectFieldWithSource(
          parseStrategyKernelValue(rawNodeValue) as StrategyKernelValue & Record<string, unknown>,
          'editorStateMap',
          mapKey,
          serializedState,
          sourceText
        );
      }
      if (mapKey.startsWith(STRATEGY_KERNEL_PLAN_SECTION_PREFIX)) {
        return mergeEditorStateMapIntoObjectFieldWithSource(
          parseStrategyKernelValue(rawNodeValue) as StrategyKernelValue & Record<string, unknown>,
          'editorStateMap',
          mapKey,
          serializedState,
          sourceText
        );
      }
      const scoped = splitStrategyKernelScopedMapKey(mapKey);
      if (scoped) {
        return updateJsonMapField(
          parseStrategyKernelValue(rawNodeValue),
          scoped.field,
          scoped.innerKey,
          serializedState,
          sourceText
        );
      }
      return undefined;
    },
    []
  );

  const {persistKey} = useDynamicNodeEditorStateMap<StrategyKernelValue>({
    editor,
    nodeKey,
    setValue,
    mergePersistedMapKey: mergePersistedField,
  });

  const persistScopedMapField = useCallback(
    (
      field: StrategyKernelEditorStateMapField,
      key: number,
      serializedState: string | undefined,
      sourceText: string | undefined
    ) => {
      persistKey(`${field}:${key}`, serializedState, sourceText);
    },
    [persistKey]
  );

  const columnHeaderLabel = useMemo((): Record<string, ReactNode> | undefined => {
    if (!planningId) {
      return undefined;
    }
    const mapJson = nodeValue.editorStateMap;
    return {
      strategy_kernel_objective: (
        <PlanningCommentField
          mapKey={planningMapKeys.strategyKernel.table.columnHeader('strategy_kernel_objective')}
          mapJson={mapJson}
          fallbackText={t(STRATEGY_KERNEL_COLUMN_LABEL_OVERRIDES.strategy_kernel_objective)}
          planningId={planningId}
          onPersistKey={persistKey}
          textBold
          fallback={(text) => (
            <Text isBold size="small">
              {text}
            </Text>
          )}
        />
      ),
      strategy_kernel_obstacles_and_opportunities: (
        <PlanningCommentField
          mapKey={planningMapKeys.strategyKernel.table.columnHeader(
            'strategy_kernel_obstacles_and_opportunities'
          )}
          mapJson={mapJson}
          fallbackText={t(
            STRATEGY_KERNEL_COLUMN_LABEL_OVERRIDES.strategy_kernel_obstacles_and_opportunities
          )}
          planningId={planningId}
          onPersistKey={persistKey}
          textBold
          fallback={(text) => (
            <Text isBold size="small">
              {text}
            </Text>
          )}
        />
      ),
      strategy_kernel_guiding_policies: (
        <PlanningCommentField
          mapKey={planningMapKeys.strategyKernel.table.columnHeader(
            'strategy_kernel_guiding_policies'
          )}
          mapJson={mapJson}
          fallbackText={t(STRATEGY_KERNEL_COLUMN_LABEL_OVERRIDES.strategy_kernel_guiding_policies)}
          planningId={planningId}
          onPersistKey={persistKey}
          textBold
          fallback={(text) => (
            <Text isBold size="small">
              {text}
            </Text>
          )}
        />
      ),
      strategy_kernel_six_month_plan: (
        <PlanningCommentField
          mapKey={planningMapKeys.strategyKernel.table.columnHeader(
            'strategy_kernel_six_month_plan'
          )}
          mapJson={mapJson}
          fallbackText={t(STRATEGY_KERNEL_COLUMN_LABEL_OVERRIDES.strategy_kernel_six_month_plan)}
          planningId={planningId}
          onPersistKey={persistKey}
          textBold
          fallback={(text) => (
            <Text isBold size="small">
              {text}
            </Text>
          )}
        />
      ),
    };
  }, [nodeValue.editorStateMap, persistKey, planningId]);

  const exportSnapshot = useMemo((): Partial<StrategyKernelValue> | null => {
    if (!switchboardWorkstream || !switchboardId) {
      return null;
    }

    const fullVisibleMetrics = (switchboardWorkstream.metrics ?? []).filter(isFullWorkstreamMetric);
    const northStarMetric = getStrategyKernelNorthStarMetric(fullVisibleMetrics, switchboardId);
    const milestoneItems = fullVisibleMetrics
      .filter((metric) => metric.type === 'milestone' && isPrimaryMetric(metric, switchboardId))
      .sort((a, b) => {
        const aTime = a.complete_date ? new Date(a.complete_date).getTime() : Infinity;
        const bTime = b.complete_date ? new Date(b.complete_date).getTime() : Infinity;
        if (aTime !== bTime) {
          return aTime - bTime;
        }
        return a.id - b.id;
      })
      .map((metric) => ({id: metric.id, text: metric.title}));

    const subworkstreamItems = switchboardWorkstream.dependencies
      .filter((dependency) => {
        const latestStatus = dependency.dependent.updates?.[0]?.status;
        return !(latestStatus && isWorkstreamStatusClosed(latestStatus));
      })
      .sort((a, b) => a.dependent.name.localeCompare(b.dependent.name))
      .map((dependency) => ({id: dependency.dependent.id, text: dependency.dependent.name}));

    return {
      objectiveFallbackText: summarizeEditorState(switchboardWorkstream.description ?? undefined),
      leadMetricItems: undefined,
      lagMetricItems: northStarMetric
        ? [{id: northStarMetric.id, text: northStarMetric.title}]
        : undefined,
      milestoneItems: milestoneItems.length > 0 ? milestoneItems : undefined,
      subworkstreamItems: subworkstreamItems.length > 0 ? subworkstreamItems : undefined,
    };
  }, [switchboardId, switchboardWorkstream]);

  const summaryObstaclesAndOpportunities = useMemo(
    () => normalizeStrategyEntries(persistedStrategySummary?.obstacles_and_opportunities),
    [persistedStrategySummary]
  );
  const summaryGuidingPrinciples = useMemo(
    () => normalizeStrategyEntries(persistedStrategySummary?.guiding_principles),
    [persistedStrategySummary]
  );

  // Keep nodeValue export cache in sync with backend data (for markdown export only)
  useEffect(() => {
    const patch: Partial<StrategyKernelValue> = {};
    let needsUpdate = false;

    // Compare by serializing to avoid infinite loop from array reference inequality
    const currentObstaclesKey = JSON.stringify(nodeValue.obstaclesAndOpportunities ?? []);
    const newObstaclesKey = JSON.stringify(summaryObstaclesAndOpportunities);
    if (currentObstaclesKey !== newObstaclesKey) {
      patch.obstaclesAndOpportunities = summaryObstaclesAndOpportunities;
      needsUpdate = true;
    }

    const currentPrinciplesKey = JSON.stringify(nodeValue.guidingPrinciples ?? []);
    const newPrinciplesKey = JSON.stringify(summaryGuidingPrinciples);
    if (currentPrinciplesKey !== newPrinciplesKey) {
      patch.guidingPrinciples = summaryGuidingPrinciples;
      needsUpdate = true;
    }

    if (needsUpdate) {
      patchNodeValue(patch);
    }
  }, [summaryObstaclesAndOpportunities, summaryGuidingPrinciples, nodeValue, patchNodeValue]);

  useEffect(() => {
    if (!exportSnapshot) {
      return;
    }

    const nextSnapshotKey = JSON.stringify(exportSnapshot);
    const currentSnapshotKey = JSON.stringify({
      objectiveFallbackText: nodeValue.objectiveFallbackText,
      leadMetricItems: nodeValue.leadMetricItems,
      lagMetricItems: nodeValue.lagMetricItems,
      milestoneItems: nodeValue.milestoneItems,
      subworkstreamItems: nodeValue.subworkstreamItems,
    });

    if (nextSnapshotKey !== currentSnapshotKey) {
      patchNodeValue(exportSnapshot);
    }
  }, [
    exportSnapshot,
    nodeValue.lagMetricItems,
    nodeValue.leadMetricItems,
    nodeValue.milestoneItems,
    nodeValue.objectiveFallbackText,
    nodeValue.subworkstreamItems,
    patchNodeValue,
  ]);

  const saveStrategyEntry = useCallback(
    async (
      entry: StrategyEntry,
      action: 'create' | 'update' | 'delete',
      strategyType: EditorDocStrategyType
    ) => {
      if (!planningId || !switchboardId) {
        return;
      }

      const valueString = entry.editor_state;
      if (action === 'create') {
        await createStrategy.mutateAsync({
          planningId,
          newStrategy: {
            strategy_type: strategyType,
            strategy_value: valueString,
          },
        });
      } else if (action === 'update' && entry.id > 0) {
        await updateStrategy.mutateAsync({
          planningId,
          strategyId: entry.id,
          updatedStrategy: {strategy_value: valueString},
        });
      } else if (action === 'delete' && entry.id > 0) {
        await deleteStrategy.mutateAsync({
          planningId,
          strategyId: entry.id,
        });
      }
    },
    [planningId, switchboardId, createStrategy, updateStrategy, deleteStrategy]
  );

  if (!planningId) {
    return (
      <Text size="small" color="subtle">
        {t('dynamic_node_workstream_table_no_planning')}
      </Text>
    );
  }

  if (!switchboardId) {
    return (
      <Text size="small" color="subtle">
        {t('dynamic_node_strategy_kernel_no_switchboard')}
      </Text>
    );
  }

  return (
    <Box className={styles.root}>
      <PlanningTableWideShell
        editor={editor}
        fillsContainer
        tableWidthPx={STRATEGY_KERNEL_WIDTH_PX}
      >
        <Table
          columns={STRATEGY_KERNEL_COLUMNS}
          data={STRATEGY_KERNEL_ROWS}
          headerTextSize="large"
          columnLabelOverrides={STRATEGY_KERNEL_COLUMN_LABEL_OVERRIDES}
          columnHeaderLabel={columnHeaderLabel}
          lastColumnResizeHandle={false}
          style={PLANNING_TABLE_SHELL_STYLE}
        >
          <Table.Header />
          <Table.Body>
            <Table.Row key="strategy_kernel">
              <Table.Cell>
                <Box className={styles.cellContent}>
                  <WorkstreamObjectiveEditableWrapper
                    workstreamId={switchboardId}
                    editorStateMapJson={nodeValue.editorStateMap}
                    onPersistKey={persistKey}
                    initialContentEditorState={nodeValue.objectiveContentEditorState}
                    handleContentEditorStateChange={(_, contentEditorState) => {
                      patchNodeValue({objectiveContentEditorState: contentEditorState});
                    }}
                  />
                </Box>
              </Table.Cell>
              <Table.Cell>
                <Box className={styles.cellContent}>
                  <StrategyEntriesRichTextListEditor
                    editor={editor}
                    planningId={planningId}
                    nodeType="obstacles-and-opportunities"
                    value={summaryObstaclesAndOpportunities}
                    editorStateMapJson={nodeValue.editorStateMap}
                    onPersistKey={persistKey}
                    onSave={(entry, action) =>
                      saveStrategyEntry(
                        entry,
                        action,
                        EditorDocStrategyType.OBSTACLES_AND_OPPORTUNITIES
                      )
                    }
                  />
                </Box>
              </Table.Cell>
              <Table.Cell>
                <Box className={styles.cellContent}>
                  <StrategyEntriesRichTextListEditor
                    editor={editor}
                    planningId={planningId}
                    nodeType="guiding-principles"
                    value={summaryGuidingPrinciples}
                    editorStateMapJson={nodeValue.editorStateMap}
                    onPersistKey={persistKey}
                    onSave={(entry, action) =>
                      saveStrategyEntry(entry, action, EditorDocStrategyType.GUIDING_PRINCIPLES)
                    }
                  />
                </Box>
              </Table.Cell>
              <Table.Cell>
                <Stack className={styles.planContent} gap="16">
                  <StrategyKernelPlanSection
                    sectionId="lagging"
                    label={t('north_star_metric')}
                    planningId={planningId}
                    mapJson={nodeValue.editorStateMap}
                    onPersistKey={persistKey}
                  >
                    <PrimaryWorkstreamMetricWrapper
                      workstreamId={switchboardId}
                      metricType="lagging"
                      typeaheadSectionLabel={t('north_star_metric').toString()}
                      typeaheadSummaryMode="baselineEoyBudget"
                      showTypeaheadMetricStatus={false}
                      showTypeaheadSectionLabel={false}
                      showTypeaheadMnpiMetricIcon
                      initialContentEditorState={nodeValue.lagMetricEditorStateMap}
                      handleContentEditorStateChange={(metricId, contentEditorState) => {
                        persistScopedMapField(
                          'lagMetricEditorStateMap',
                          metricId,
                          contentEditorState,
                          undefined
                        );
                      }}
                      planningCollabExtras={{
                        scopedField: 'lagMetricEditorStateMap',
                        persistScopedFullKey: persistKey,
                      }}
                    />
                  </StrategyKernelPlanSection>
                  <StrategyKernelPlanSection
                    sectionId="milestones"
                    label={t('milestones')}
                    planningId={planningId}
                    mapJson={nodeValue.editorStateMap}
                    onPersistKey={persistKey}
                  >
                    <StarredMilestonesWrapper
                      workstreamId={switchboardId}
                      initialContentEditorState={nodeValue.milestonesEditorStateMap}
                      handleContentEditorStateChange={(metricId, contentEditorState) => {
                        persistScopedMapField(
                          'milestonesEditorStateMap',
                          metricId,
                          contentEditorState,
                          undefined
                        );
                      }}
                      planningCollabExtras={{
                        scopedField: 'milestonesEditorStateMap',
                        persistScopedFullKey: persistKey,
                      }}
                    />
                  </StrategyKernelPlanSection>
                  <StrategyKernelPlanSection
                    sectionId="subworkstreams"
                    label={t('sub_workstreams')}
                    planningId={planningId}
                    mapJson={nodeValue.editorStateMap}
                    onPersistKey={persistKey}
                  >
                    <SubworkstreamSelectionWrapper
                      editor={editor}
                      workstreamId={switchboardId}
                      initialContentEditorState={nodeValue.subworkstreamsEditorStateMap}
                      handleContentEditorStateChange={(
                        workstreamIdForState,
                        contentEditorState
                      ) => {
                        persistScopedMapField(
                          'subworkstreamsEditorStateMap',
                          workstreamIdForState,
                          contentEditorState,
                          undefined
                        );
                      }}
                      onPersistSubworkstreamDri={(subWorkstreamId, serializedState, sourceText) => {
                        persistKey(
                          `subworkstreamsEditorStateMap:${subWorkstreamId}:dri`,
                          serializedState,
                          sourceText
                        );
                      }}
                      onPersistSubworkstreamNameAndDri={(
                        subWorkstreamId,
                        serializedState,
                        sourceText
                      ) => {
                        persistKey(
                          `subworkstreamsEditorStateMap:${planningMapKeys.workstreamTable.nameAndDri(
                            subWorkstreamId
                          )}`,
                          serializedState,
                          sourceText
                        );
                      }}
                      combineNameAndDriComments
                      hideAddSubworkstream
                    />
                  </StrategyKernelPlanSection>
                </Stack>
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </PlanningTableWideShell>
    </Box>
  );
};

const StrategyKernelPlanSection = ({
  sectionId,
  label,
  planningId,
  mapJson,
  onPersistKey,
  hideLabel = false,
  children,
}: {
  sectionId: 'leading' | 'lagging' | 'milestones' | 'subworkstreams';
  label: string;
  planningId: string;
  mapJson: string | undefined;
  onPersistKey: (
    key: string,
    serialized: string | undefined,
    sourceText: string | undefined
  ) => void;
  hideLabel?: boolean;
  children: ReactNode;
}) => {
  const infoTooltip =
    sectionId === 'lagging'
      ? t('strategy_kernel_north_star_metric_info').toString()
      : sectionId === 'milestones'
        ? t('strategy_kernel_milestones_info').toString()
        : null;

  return (
    <Box className={styles.planSection}>
      {hideLabel ? null : (
        <Box className={styles.sectionLabelRow}>
          <PlanningCommentField
            mapKey={`${STRATEGY_KERNEL_PLAN_SECTION_PREFIX}${sectionId}`}
            mapJson={mapJson}
            fallbackText={label}
            planningId={planningId}
            onPersistKey={onPersistKey}
            textBold
            fallback={(text) => (
              <Text size="small" isBold className={styles.sectionLabel}>
                {text}
              </Text>
            )}
          />
          {infoTooltip ? (
            <Tooltip title={infoTooltip} placement="top">
              <span
                className={styles.sectionInfoIcon}
                aria-label={infoTooltip}
                role="img"
                tabIndex={0}
              >
                <UIIcon src={InfoLine} size="small" />
              </span>
            </Tooltip>
          ) : null}
        </Box>
      )}
      <Box>{children}</Box>
    </Box>
  );
};

export const strategyKernelDefinition: DynamicNodeDefinition = {
  kindId: STRATEGY_KERNEL_KIND,
  labelKey: 'dynamic_node_strategy_kernel',
  categoryKey: 'dynamic_node_category_workstreams',
  defaultBinding: {kind: STRATEGY_KERNEL_KIND} satisfies StrategyKernelBinding,
  Component: StrategyKernel,
  exportHtml: ({value}) => exportStrategyKernel(value),
};
